import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AssignedShopsResponse, AssignShopRequest, CreateMarketAreaRequest, MarketAreaDailyReport, MarketAreaResponse, ShopAssignmentHistory, UnassignShopResponse, UpdateShopGMResponse, UserSetup, UserSetupResponse } from '@org/interfaces';

@Injectable({
  providedIn: 'root'
})
export class MarketAreaService {
  private apiUrl = `${environment.apiUrl}/api/market-areas`;

  constructor(private http: HttpClient) {}

  /**
   * Create a new market area
   * @param marketArea Market area creation data
   */
  createMarketArea(marketArea: CreateMarketAreaRequest): Observable<MarketAreaResponse> {
    return this.http.post<MarketAreaResponse>(`${this.apiUrl}`, marketArea).pipe(
        catchError(error => {
          console.error('Error fetching assigned shops:', error);
          return throwError(() => new Error(error.error?.message || 'Failed to fetch assigned shops'));
        })
      );
  }

  /**
   * Get all shops assigned to the current AM
   */
  getAssignedShops(): Observable<AssignedShopsResponse> {
    return this.http.get<AssignedShopsResponse>(`${this.apiUrl}/shops`).pipe(
        catchError(error => {
          console.error('Error fetching assigned shops:', error);
          return throwError(() => new Error(error.error?.message || 'Failed to fetch assigned shops'));
        })
      );
    }

  /**
   * Get aggregated daily report for all shops in the market area
   * @param date Date for the report (YYYY-MM-DD format)
   */
  getAggregatedDailyReports(date: string): Observable<MarketAreaDailyReport> {
    return this.http.get<MarketAreaDailyReport>(
      `${this.apiUrl}/daily-reports?date=${date}`
    ).pipe(
        catchError(error => {
          console.error('Error fetching aggregated daily reports:', error);
          return throwError(() => new Error(error.error?.message || 'Failed to fetch aggregated daily reports'));
        })
      );
  }

  /**
   * Assign a shop to the current AM's market area
   * @param payload Object containing shopId and generalManagerId
   */
  assignShop(payload: AssignShopRequest): Observable<MarketAreaResponse> {
    return this.http.post<MarketAreaResponse>(`${this.apiUrl}/assign-shop`, payload).pipe(
        catchError(error => {
          console.error('Error assigning shop:', error);
          return throwError(() => new Error(error.error?.message || 'Failed to assign shop'));
        })
      );
  }

/**
   * Unassign a shop from the market area
   * @param shopId ID of the shop to unassign
   */
  unassignShop(shopId: string): Observable<UnassignShopResponse> {
    return this.http.delete<UnassignShopResponse>(`${this.apiUrl}/shops/${shopId}`);
    }

/**
   * Update the General Manager for a shop
   * @param shopId ID of the shop
   * @param generalManagerId ID of the new general manager
   */
  updateShopGM(shopId: string, generalManagerId: string): Observable<UpdateShopGMResponse> {
    return this.http.patch<UpdateShopGMResponse>(
        `${this.apiUrl}/shops/${shopId}/gm`,
        { generalManagerId }
    );
  }

  /**
   * Get assignment history for a shop
   * @param shopId ID of the shop
   */
  getShopAssignmentHistory(shopId: string): Observable<ShopAssignmentHistory[]> {
    return this.http.get<ShopAssignmentHistory[]>(`${this.apiUrl}/shops/${shopId}/history`).pipe(
      catchError(error => {
        console.error('Error fetching shop assignment history:', error);
        return throwError(() => new Error(error.error?.message || 'Failed to fetch shop assignment history'));
      })
    );
  }

  /**
   * Check if AM user needs to complete setup steps
   */
  checkAMSetupStatus(): Observable<UserSetupResponse> {
    return this.http.get<UserSetupResponse>(`${this.apiUrl}/setup-status`).pipe(
      catchError(error => {
        console.error('Error checking AM setup status:', error);
        return throwError(() => new Error(error.error?.message || 'Failed to check setup status'));
      })
    );
  }
}

